import AttachFileIcon from '@mui/icons-material/AttachFile';
import { Box, Chip, Link, Paper, Typography } from '@mui/material';
import strings from 'common/Translation/Translate';
import dayjs from 'dayjs';
import { FC } from 'react';
import { MarkCompleteButton } from 'screens/Authenticated/TaskList/MarkCompleteButton';
import Config from 'utils/Config';

export type FollowUpEntry = {
  id: number;
  createdBy?: string;
  createdAt?: string;
  datetime?: string;
  mode?: string;
  assignedTo?: string;
  summary?: string;
  attachmentUrl?: string;
  fileName?: string;
  fileSize?: number;
  status?: string;
};

const formatFileSize = (bytes?: number | null) => {
  if (bytes == null || !Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) {
    const kb = bytes / 1024;
    return `${kb < 10 ? kb.toFixed(1) : Math.round(kb)} KB`;
  }
  const mb = bytes / (1024 * 1024);
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
};

const fileLabel = (entry: FollowUpEntry) => {
  const name = String(entry.fileName || '').trim() || strings.paymentDetails_text_attachment;
  const size = formatFileSize(entry.fileSize);
  return size ? `${name} · ${size}` : name;
};

const formatMode = (mode?: string) =>
  String(mode || '')
    .replaceAll('_', ' ')
    .toLowerCase()
    .replace(/\b\w/g, c => c.toUpperCase());

export const FollowUpTimeline: FC<{
  followUps: FollowUpEntry[];
  onComplete: () => void;
}> = ({ followUps, onComplete }) => {
  if (!followUps.length) return null;

  return (
    <Box sx={{ pt: 1, pb: 0.5 }}>
      <Typography sx={{ fontWeight: 600, fontSize: 14, mb: 1.5 }}>{strings.booking_logs_follow_up_timeline}</Typography>
      {followUps.map((entry, index) => {
        const isLast = index === followUps.length - 1;
        const isPending = String(entry.status || '').toUpperCase() === 'PENDING';
        const isCompleted = String(entry.status || '').toUpperCase() === 'COMPLETED';
        const dateLabel = entry.datetime ? dayjs(entry.datetime).format('D MMM YYYY') : '';
        const timeLabel = entry.createdAt ? dayjs(entry.createdAt).format('hh:mm A') : '';
        const headerBits = [dateLabel, timeLabel].filter(Boolean);

        return (
          <Box key={entry.id ?? `${entry.createdAt}-${index}`} sx={{ display: 'flex', alignItems: 'stretch', gap: 1.5 }}>
            <Box
              sx={{
                width: 22,
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}>
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  fontSize: 11,
                  fontWeight: 700,
                  lineHeight: '22px',
                  textAlign: 'center',
                  flexShrink: 0,
                }}>
                {index + 1}
              </Box>
              {!isLast && (
                <Box
                  sx={{
                    width: 2,
                    flex: 1,
                    bgcolor: 'divider',
                    my: 0.5,
                    borderRadius: 1,
                    minHeight: 12,
                  }}
                />
              )}
            </Box>
            <Paper
              elevation={0}
              sx={{
                flex: 1,
                minWidth: 0,
                mb: isLast ? 0.5 : 2,
                px: 1.75,
                py: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.5,
                bgcolor: 'background.paper',
              }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1, flexWrap: 'wrap' }}>
                  {headerBits.length ? (
                    <Typography sx={{ fontSize: 12, fontWeight: 500, color: 'text.secondary', lineHeight: 1.4 }}>
                      {headerBits.join(' . ')}
                    </Typography>
                  ) : null}
                  <Chip
                    size="small"
                    label={
                      isCompleted
                        ? strings.booking_logs_status_completed
                        : isPending
                          ? strings.booking_logs_status_pending
                          : String(entry.status || '').toUpperCase()
                    }
                    color={isCompleted ? 'success' : 'warning'}
                    sx={{ height: 22, fontWeight: 700, fontSize: 11, '& .MuiChip-label': { px: 0.75 } }}
                  />
                </Box>
                {isPending && entry.id != null ? (
                  <Box sx={{ ml: 'auto', flexShrink: 0 }}>
                    <MarkCompleteButton id={entry.id} onSuccess={onComplete} />
                  </Box>
                ) : null}
              </Box>
              <Typography sx={{ mt: 0.75, fontSize: 13, lineHeight: 1.5 }}>
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  {strings.booking_logs_assigned_to_label}:
                </Box>{' '}
                {entry.assignedTo || '—'}
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  {' '}
                  | {strings.booking_logs_mode_label}:
                </Box>{' '}
                {formatMode(entry.mode) || '—'}
              </Typography>
              <Typography sx={{ mt: 0.25, fontSize: 13, lineHeight: 1.5 }}>
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  {strings.booking_logs_created_by_label}:
                </Box>{' '}
                {String(entry.createdBy || '').trim() || 'anonymous'}
              </Typography>
              {entry.summary ? (
                <Box sx={{ mt: 0.75 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>
                    {strings.booking_logs_summary}:
                  </Typography>
                  <Typography sx={{ mt: 0.25, fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {entry.summary}
                  </Typography>
                </Box>
              ) : null}
              {entry.attachmentUrl ? (
                <Link
                  href={`${Config.BaseURL}${entry.attachmentUrl}`}
                  target="_blank"
                  rel="noopener"
                  underline="hover"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    mt: 0.75,
                    px: 1,
                    py: 0.35,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    fontSize: 12,
                    fontWeight: 600,
                    maxWidth: '100%',
                  }}>
                  <AttachFileIcon sx={{ fontSize: 16, transform: 'rotate(45deg)', flexShrink: 0 }} />
                  <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {fileLabel(entry)}
                  </Box>
                </Link>
              ) : null}
            </Paper>
          </Box>
        );
      })}
    </Box>
  );
};
